/**
 * Arbitrage entre une écriture SERVEUR arrivée pendant que l'écran est monté et
 * le brouillon que l'avocat a peut-être en cours dans le même champ.
 *
 * WHY ce fichier : les écrans « consigne » de la Personnalisation (dictée vocale,
 * libellés des pièces) sont écrits par DEUX auteurs — l'avocat au clavier, et
 * Hector via ses outils (`set_voice_transcription_prompt`,
 * `set_piece_naming_rules`). Tant que la barre « Demander à Hector » naviguait
 * vers `/assistant`, le retour démontait l'écran et le remontage relisait la
 * valeur fraîche. Depuis que la barre ouvre le dock SANS quitter la page, plus
 * rien ne démonte : il faut donc décider, à chaque réponse de query, si l'on
 * peut ré-amorcer le champ.
 *
 * La règle est la même des deux côtés et vit ici pour qu'elle ne puisse pas
 * diverger entre les deux implémentations (l'une dans un hook, l'autre dans le
 * state local d'un onglet).
 */

/**
 * Décision à appliquer au champ.
 *
 * - `adopt` : le champ est propre, on y écrit la valeur du serveur.
 * - `offer` : l'avocat a un brouillon non enregistré — on ne touche à RIEN et on
 *   lui propose la nouvelle version. Écraser sa frappe est inacceptable ;
 *   afficher une valeur périmée sans le dire l'est tout autant.
 * - `ignore` : le serveur ne dit rien de nouveau.
 */
export type ExternalDraftResolution =
  | { readonly kind: "adopt"; readonly value: string }
  | { readonly kind: "offer"; readonly value: string }
  | { readonly kind: "ignore" };

export interface ExternalDraftUpdateInput {
  /** Valeur qui vient d'arriver du serveur. */
  readonly incoming: string;
  /** Dernière valeur connue comme enregistrée (celle sur laquelle se calcule `isDirty`). */
  readonly saved: string;
  /** Contenu courant du champ. `draft === saved` ⇔ aucun brouillon non enregistré. */
  readonly draft: string;
}

/**
 * Décide du sort d'une valeur serveur fraîche face au champ à l'écran.
 *
 * L'égalité `incoming === saved` est testée AVANT l'état du brouillon : c'est le
 * cas de très loin le plus fréquent (tout refetch d'arrière-plan qui ne rapporte
 * aucun changement), et c'est aussi ce qui évite de proposer « charger la
 * nouvelle version » alors que la version en question est déjà celle qu'on a.
 */
export function resolveExternalDraftUpdate({
  incoming,
  saved,
  draft,
}: ExternalDraftUpdateInput): ExternalDraftResolution {
  if (incoming === saved) return { kind: "ignore" };
  if (draft === saved) return { kind: "adopt", value: incoming };
  return { kind: "offer", value: incoming };
}
